import type {
  ApplyResultSummary,
  PresentationContent,
  PresentationModification,
  PreslinkChartTarget,
  PreslinkChartToolInput,
  ShapeContent,
  ShapeModification,
} from "../../types";
import type { FactsetChartParameters } from "../../../shared/factset/chartCreatorTypes";
import {
  ChartCreatorValidationError,
  assertChartParametersValid,
  normalizeActiveGraphChartParameters,
} from "../../../shared/factset/chartCreatorPayload";
import {
  downloadExcelChartViaApi,
  getStylesetsViaApi,
  previewChartViaApi,
} from "../../../shared/factset/chartCreatorApi";
import {
  findStyleset,
  mergeChartFormat,
  readStylesetPreview,
  type StylesetLook,
} from "../../../shared/factset/stylesetChartFormat";
import {
  isChartCreatorReauthRequired,
  resolveChartCreatorMode,
  withChartCreatorReauthRetry,
} from "../../../shared/factset/chartCreatorMode";
import {
  applyModificationsViaIntents,
  extractPresentationContentViaIntents,
  getPreslinkCreationPresentationContextViaIntents,
  getPreslinkFilePathsViaIntents,
  getSelectedShapesViaIntents,
  RUN_OFFICE_EXTRACTOR_MINIMUM_SHAPE_FILTER,
  writeFileFromBase64WithOutcomeViaIntents,
  type FactSetSelectedShapeRef,
  type PreslinkCreationPresentationContext,
} from "../factset/intentBridgeService";
import {
  PreslinkCreateDataError,
  createPreslinkData,
  renderPreslinkTags,
  type PreslinkCreatedShape,
} from "./preslinkCreateDataApi";
import {
  addMissingPreslinkSheetNames,
  buildCreateDataShapes,
  discoverPreslinkWorkbookTargets,
  findLinkedPictures,
  frameAroundFittedPictures,
  layoutPreslinkOutputs,
  parsePreslinkTagMainFields,
  recolorLinkedPictureText,
  replaceLinkedPictureSnapshots,
  type PreslinkLinkedPicture,
  type PreslinkOutputPlacement,
  type PreslinkWorkbookGeometry,
  type PreslinkWorkbookTarget,
} from "./preslinkWorkbook";
import {
  PreslinkPreflightError,
  assertPreslinkDeckUsable,
  buildPreslinkWorkbookFileName,
  createPreslinkTimestampSequence,
  isCreatedPreslinkWorkbookName,
  type PreslinkDeckLocation,
} from "./preslinkChartPreflight";
import { redactLocalPathText, withLocalPathRedaction } from "../../../shared/utils/localPathRedaction";
import { fitChartCreatorValueAxes } from "./preslinkChartAxis";
import { repairChartCreatorLabels } from "./preslinkChartLabels";
import { applyStylesetLook, widenHairlineSeries } from "./preslinkChartTheme";
import { toTransparentSnapshotPng } from "./preslinkSnapshotImage";
import {
  assertValidChartTarget,
  clampChartFrameUpdate,
  clampNewChartFrame,
  hasFrameFields,
  usableSlideBounds,
  type ChartFrame,
  type ChartSlideBounds,
} from "./chartFrameBounds";
import type { AgentToolContext } from "./types";

/**
 * Holds the chartParameters a linked chart was built from, so a later turn can
 * rebuild it when a chart setting changes; data-only changes refresh it in
 * place. Chat history keeps only text, and the workbook name cannot carry
 * dates, frequency, or formatting.
 */
const PRESLINK_CHART_PARAMETERS_TAG = "FINSTER_PRESLINK_CHART";

/**
 * The frame a linked chart was asked to fill. Its picture can be smaller, and
 * replacing the chart from the picture's bounds would shrink it every time.
 */
const PRESLINK_CHART_FRAME_TAG = "FINSTER_PRESLINK_FRAME";

/**
 * The workbook file name a linked chart was created with. A PresLink refresh
 * saves under a new name of the same shape, so only a change from this name
 * shows the chart has been refreshed.
 */
const PRESLINK_CHART_WORKBOOK_TAG = "FINSTER_PRESLINK_WORKBOOK";

/** Chart size when no selected or replaced shape supplies a frame. */
const DEFAULT_FRAME_SIZE = { width: 480, height: 280 };

const PRESLINK_TARGET_RULES = {
  invalidCode: "preslink_chart_frame_invalid",
  label: "Linked chart",
  otherFields: ["slideId", "shapeId"],
};

/** Used only when the host reports no slide size. */
const FALLBACK_SLIDE_SIZE = { slideWidth: 960, slideHeight: 540 };

/** Geometry tolerance in points for readback verification. */
const GEOMETRY_TOLERANCE_PT = 1;

/**
 * Bounded because each attempt costs a full create-data round-trip. Ten
 * consecutive same-second collisions means something other than our own
 * timestamps is producing the names, so retrying further would not help.
 */
const MAX_SAVE_ATTEMPTS = 10;

export interface PreslinkChartToolResult {
  ok: boolean;
  code: string;
  stage: string;
  applied: boolean;
  partialMutation: boolean;
  /** A chosen Preslink route never falls back, so this is always false. */
  fallbackAllowed: false;
  message?: string;
  warning?: string;
  data?: Record<string, unknown>;
}

type Stage =
  | "preflight"
  | "download"
  | "targets"
  | "create_data"
  | "patch_names"
  | "verify_render"
  | "save"
  | "insert"
  | "readback"
  | "replace_target";

/** Results reach the model, Langfuse and saved chat history, so no text in them may name a local path. */
function result(
  overrides: Partial<PreslinkChartToolResult> &
    Pick<PreslinkChartToolResult, "ok" | "code" | "stage">,
): PreslinkChartToolResult {
  const { message, warning } = overrides;
  return {
    applied: false,
    partialMutation: false,
    fallbackAllowed: false,
    ...overrides,
    ...(message !== undefined ? { message: redactLocalPathText(message) } : {}),
    ...(warning !== undefined ? { warning: redactLocalPathText(warning) } : {}),
  };
}

/**
 * The Excel export ignores stylesetId, so the requested styleset is carried as
 * the chartFormat and workbook look FactSet itself defines for it on this
 * chart type.
 */
async function applyRequestedStyleset(
  parameters: FactsetChartParameters,
  signal: AbortSignal | undefined,
): Promise<
  | { parameters: FactsetChartParameters; stylesetName?: string; look?: StylesetLook }
  | { failure: PreslinkChartToolResult }
> {
  const requested = parameters.stylesetId?.trim();
  if (!requested) return { parameters };
  const { stylesets } = await getStylesetsViaApi({ signal });
  const styleset = findStyleset(stylesets, requested);
  if (!styleset) {
    return {
      failure: result({
        ok: false,
        code: "preslink_chart_styleset_not_found",
        stage: "preflight",
        message: `FactSet has no chart theme "${requested}" for this account. Available themes: ${
          stylesets.map((candidate) => candidate.name).join(", ") || "none"
        }.`,
      }),
    };
  }
  const preview = await previewChartViaApi(
    { ...parameters, stylesetId: styleset.id },
    { generatePreview: "None", signal },
  );
  const definition = readStylesetPreview(preview.fdsChartXml ?? "");
  if (!definition) {
    return {
      failure: result({
        ok: false,
        code: "preslink_chart_styleset_unreadable",
        stage: "preflight",
        message: `FactSet did not return the definition of the ${styleset.name} theme, so no chart was created.`,
      }),
    };
  }
  return {
    parameters: {
      ...parameters,
      stylesetId: styleset.id,
      chartFormat: mergeChartFormat(definition.chartFormat, parameters.chartFormat),
    },
    stylesetName: styleset.name,
    look: definition.look,
  };
}

interface PreslinkSourceWorkbook {
  workbookBase64: string;
  stylesetName?: string;
  look?: StylesetLook;
}

/**
 * Chart Creator's workbook for these parameters, with the requested styleset
 * and the local repairs every linked chart gets. A reauthorization answer from
 * either Chart Creator call reconnects once and reruns both: nothing has been
 * written at this point, so a rerun cannot duplicate anything.
 */
async function downloadPreslinkSourceWorkbook(
  parameters: FactsetChartParameters,
  options: { signal?: AbortSignal; onDownload?: () => void } = {},
): Promise<PreslinkSourceWorkbook | { failure: PreslinkChartToolResult }> {
  const fetched = await withChartCreatorReauthRetry(async () => {
    const styled = await applyRequestedStyleset(parameters, options.signal);
    if ("failure" in styled) return styled;
    options.onDownload?.();
    const download = await downloadExcelChartViaApi(styled.parameters, { signal: options.signal });
    return { styled, workbookBase64: download.workbookBase64 };
  });
  if ("failure" in fetched) return fetched;
  const { look, stylesetName } = fetched.styled;
  const fitted = await fitChartCreatorValueAxes(fetched.workbookBase64);
  const weighted = await widenHairlineSeries(fitted.workbookBase64);
  const themed = look ? await applyStylesetLook(weighted, look) : weighted;
  const labelled = await repairChartCreatorLabels(themed);
  return { workbookBase64: labelled.workbookBase64, stylesetName, look };
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function assertNotCancelled(signal: AbortSignal | undefined, stage: Stage): void {
  if (!signal?.aborted) return;
  const error = new Error(`Preslink chart creation cancelled before ${stage}.`);
  error.name = "AbortError";
  throw error;
}

/** Tickers in request order, so the filename reads the way the user asked. */
function readIdentifiers(parameters: FactsetChartParameters): string[] {
  return (parameters.tickers ?? []).filter(
    (ticker): ticker is string => typeof ticker === "string" && ticker.trim().length > 0,
  );
}

function readChartCode(parameters: FactsetChartParameters): string {
  const chartType = parameters.chartType?.trim();
  return chartType ? chartType : "CHART";
}

function defaultFrame(
  slideSize: { slideWidth: number; slideHeight: number },
): PreslinkWorkbookGeometry {
  const { width, height } = DEFAULT_FRAME_SIZE;
  return {
    left: Math.max(0, Math.round((slideSize.slideWidth - width) / 2)),
    top: Math.max(0, Math.round((slideSize.slideHeight - height) / 2)),
    width,
    height,
  };
}

/**
 * The frame the model asked for, clamped inside the slide the way ActiveGraph
 * clamps its own. Over a replaced chart, values it left out come from that
 * chart's frame; for a new chart, size defaults to 480 x 280, centred.
 */
function explicitFrame(
  requested: Partial<ChartFrame>,
  replacing: { frame: PreslinkWorkbookGeometry | undefined } | undefined,
  slideSize: ChartSlideBounds,
): PreslinkWorkbookGeometry {
  if (replacing) {
    const merged = clampChartFrameUpdate(requested, replacing.frame ?? {}, slideSize, {
      code: "preslink_chart_frame_unavailable",
      message:
        "The shape to replace did not report its position and size. Give left, top, width and height together.",
    });
    if (merged) return merged;
  }
  const slide = usableSlideBounds(slideSize);
  const width = requested.width ?? DEFAULT_FRAME_SIZE.width;
  const height = requested.height ?? DEFAULT_FRAME_SIZE.height;
  return clampNewChartFrame(requested, slide, {
    left: Math.round((slide.slideWidth - width) / 2),
    top: Math.round((slide.slideHeight - height) / 2),
    width,
    height,
  });
}

/** Shapes that already fill a placeholder, so it has no room left for the chart. */
const PLACEHOLDER_CONTENT_TYPES = new Set<ShapeContent["type"]>(["image", "chart", "oleObject", "table", "group", "line"]);

/**
 * PowerPoint fills an empty content placeholder with an inserted picture, and
 * an ActiveGraph is placed on that placeholder too, so the chart takes its
 * frame. On layouts with several, the largest wins.
 */
function emptyContentPlaceholder(shapes: readonly ShapeContent[]): ShapeContent | undefined {
  let largest: ShapeContent | undefined;
  for (const shape of shapes) {
    const kind = shape.placeholderType?.toLowerCase().replace(/[\s_-]+/g, "") ?? "";
    if (!/body|object|content/.test(kind) || kind.includes("title")) continue;
    if (PLACEHOLDER_CONTENT_TYPES.has(shape.type) || shape.text?.trim() || !shapeFrame(shape)) continue;
    if (!largest || shape.width * shape.height > largest.width * largest.height) largest = shape;
  }
  return largest;
}

function shapeFrame(
  shape: Partial<Record<keyof PreslinkWorkbookGeometry, unknown>>,
): PreslinkWorkbookGeometry | undefined {
  const { left, top, width, height } = shape;
  if (
    typeof left !== "number" ||
    typeof top !== "number" ||
    typeof width !== "number" ||
    typeof height !== "number" ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }
  return { left, top, width, height };
}

function unionFrame(shapes: readonly ShapeContent[]): PreslinkWorkbookGeometry | undefined {
  const frames = shapes.map(shapeFrame);
  if (frames.length === 0 || frames.some((frame) => !frame)) return undefined;
  const safe = frames as PreslinkWorkbookGeometry[];
  const left = Math.min(...safe.map((frame) => frame.left));
  const top = Math.min(...safe.map((frame) => frame.top));
  return {
    left,
    top,
    width: Math.max(...safe.map((frame) => frame.left + frame.width)) - left,
    height: Math.max(...safe.map((frame) => frame.top + frame.height)) - top,
  };
}

/**
 * The frame the chart being replaced was asked to fill, following any move or
 * resize of its pictures. Shapes without a stored frame give their bounds.
 */
function replacedFrame(shapes: readonly ShapeContent[]): PreslinkWorkbookGeometry | undefined {
  const bounds = unionFrame(shapes);
  const stored = shapes.map(storedFrame).find((frame) => frame !== undefined);
  return bounds && stored ? frameAroundFittedPictures(bounds, stored) : bounds;
}

function storedFrame(shape: ShapeContent): PreslinkWorkbookGeometry | undefined {
  const stored = readTag(shape.tags, PRESLINK_CHART_FRAME_TAG);
  if (!stored) return undefined;
  try {
    const frame: unknown = JSON.parse(stored);
    return frame && typeof frame === "object" ? shapeFrame(frame) : undefined;
  } catch {
    return undefined;
  }
}

/** Workbook behind a linked chart this tool created, or undefined for any other shape. */
function createdChartWorkbook(shape: ShapeContent): string | undefined {
  const linkTag = readTag(shape.tags, "DMSOFTTAG");
  if (!linkTag || !readTag(shape.tags, PRESLINK_CHART_PARAMETERS_TAG)) return undefined;
  try {
    const { path, fileName } = parsePreslinkTagMainFields({ DMSOFTTAG: linkTag });
    return `${path}\\${fileName}`.toLowerCase();
  } catch {
    return undefined;
  }
}

/**
 * Some chart types are several pictures over one workbook (an overlay on its
 * base chart), so replacing any of them replaces the whole chart.
 */
function linkedChartPictures(
  shapes: readonly ShapeContent[],
  target: ShapeContent,
): ShapeContent[] {
  const workbook = createdChartWorkbook(target);
  if (!workbook) return [target];
  return shapes.filter((shape) => createdChartWorkbook(shape) === workbook);
}

/**
 * The chartParameters a picture this tool created was built from, while its
 * link still points at the workbook made from them. A PresLink refresh moves
 * the link to a new workbook, after which they describe data the chart no
 * longer shows. Charts created before the workbook tag fall back to the name
 * pattern.
 */
function readFreshCreatedChartParameters(
  shape: ShapeContent,
): Record<string, unknown> | undefined {
  const stored = readTag(shape.tags, PRESLINK_CHART_PARAMETERS_TAG);
  const linkTag = readTag(shape.tags, "DMSOFTTAG");
  if (!stored || !linkTag) return undefined;
  try {
    const parameters: unknown = JSON.parse(stored);
    if (!parameters || typeof parameters !== "object" || Array.isArray(parameters)) {
      return undefined;
    }
    const { fileName } = parsePreslinkTagMainFields({ DMSOFTTAG: linkTag });
    const createdFileName = readTag(shape.tags, PRESLINK_CHART_WORKBOOK_TAG);
    const linkedToCreatedWorkbook = createdFileName
      ? createdFileName.trim().toLowerCase() === fileName.trim().toLowerCase()
      : isCreatedPreslinkWorkbookName(
          fileName,
          readChartCode(parameters as FactsetChartParameters),
        );
    return linkedToCreatedWorkbook ? (parameters as Record<string, unknown>) : undefined;
  } catch {
    // A tag that is not ours to parse marks nothing.
    return undefined;
  }
}

/**
 * Linked charts this tool created on a slide, keyed by shape id, with the
 * chartParameters each was built from. Charts refreshed since are left out.
 * Turn-start extraction skips tags for speed, so this reads just the one slide.
 */
export async function readCreatedPreslinkCharts(
  slideId: string,
  ctx: AgentToolContext,
): Promise<Map<string, Record<string, unknown>>> {
  const presentation = await extractPresentationContentViaIntents(withLocalPathRedaction(ctx.log), {
    forceRefresh: true,
    presentationRef: ctx.presentationRef,
    filters: {
      slides: {
        ids: [slideId],
        shapes: { ...RUN_OFFICE_EXTRACTOR_MINIMUM_SHAPE_FILTER, tags: true },
      },
    },
  });
  const charts = new Map<string, Record<string, unknown>>();
  for (const shape of findSlide(presentation, slideId)?.shapes ?? []) {
    const parameters = readFreshCreatedChartParameters(shape);
    if (parameters) charts.set(shape.id, parameters);
  }
  return charts;
}

export function applyCreatedPreslinkChartMarkers(
  content: PresentationContent,
  slideId: string,
  charts: ReadonlyMap<string, Record<string, unknown>>,
): PresentationContent {
  if (charts.size === 0) return content;
  return {
    ...content,
    slides: content.slides.map((slide) =>
      slide.id !== slideId
        ? slide
        : {
            ...slide,
            shapes: slide.shapes.map((shape) => {
              const chartParameters = charts.get(shape.id);
              return chartParameters ? { ...shape, preslinkChart: { chartParameters } } : shape;
            }),
          },
    ),
  };
}

function resolveSlideSize(
  presentation: PresentationContent | undefined,
  ctx: AgentToolContext,
): { slideWidth: number; slideHeight: number } {
  if (presentation && presentation.slideWidth > 0 && presentation.slideHeight > 0) {
    return { slideWidth: presentation.slideWidth, slideHeight: presentation.slideHeight };
  }
  return ctx.slideBounds ?? FALLBACK_SLIDE_SIZE;
}

/**
 * A missing or failed selection read means "nothing selected": the chart then
 * gets the default frame instead of failing the whole creation.
 */
async function readTurnSelectedShapes(
  ctx: AgentToolContext,
): Promise<FactSetSelectedShapeRef[]> {
  const pending =
    ctx.preslinkTurnSelectedShapes ??
    (ctx.preslinkTurnSelectedShapes = getSelectedShapesViaIntents({
      log: withLocalPathRedaction(ctx.log),
    }));
  try {
    return (await pending).shapes;
  } catch {
    return [];
  }
}

function findSlide(
  presentation: PresentationContent | undefined,
  slideId: string | undefined,
): { id: string; shapes: ShapeContent[] } | undefined {
  if (!presentation) return undefined;
  const slide = slideId
    ? presentation.slides.find((entry) => entry.id === slideId)
    : presentation.slides.find(
        (entry) => entry.index === presentation.selectedSlideIndex,
      ) ?? presentation.slides[presentation.selectedSlideIndex];
  return slide ? { id: slide.id, shapes: slide.shapes ?? [] } : undefined;
}

/**
 * Stable key so a replayed tool call in the same turn does not create twice.
 * Built from the normalized parameters with sorted keys, so a retry that only
 * reorders or re-cases its arguments still finds the earlier result.
 */
function replayKey(
  parameters: FactsetChartParameters,
  target: PreslinkChartTarget | undefined,
): string {
  return JSON.stringify(canonicalize({ parameters, target: target ?? null }));
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, nested]) => nested !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, nested]) => [key, canonicalize(nested)]),
  );
}

/** chartParameters a PresLink refresh changes in place; every other field is a chart setting. */
const PRESLINK_DATA_FIELDS: ReadonlySet<string> = new Set([
  "tickers",
  "companyNames",
  "startDate",
  "endDate",
  "frequency",
]);

function isEmptySetting(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === "object" && Object.keys(canonicalize(value) as object).length === 0;
}

/**
 * Series colors follow tickers[] order, so the colors of peers that only one
 * side has come and go with the peer set rather than with the chart's look.
 */
function sharedSeriesFormat(format: unknown, seriesCount: number): unknown {
  if (!format || typeof format !== "object") return format;
  const { seriesColor, ...rest } = format as Record<string, unknown>;
  if (!seriesColor || typeof seriesColor !== "object") return format;
  const shared = Object.entries(seriesColor).filter(([key]) => {
    const index = /^seriesColor(\d+)$/.exec(key)?.[1];
    return index === undefined || Number(index) < seriesCount;
  });
  return shared.length > 0 ? { ...rest, seriesColor: Object.fromEntries(shared) } : rest;
}

/**
 * Compares the chart settings of a call with those a created chart was built
 * from. A setting the call leaves out counts as unchanged, so removing one
 * takes an explicit empty value.
 */
function compareChartSettings(
  requested: FactsetChartParameters,
  stored: FactsetChartParameters,
): { changed: boolean; omittedSettings: string[] } {
  const seriesCount = Math.min(requested.tickers.length, stored.tickers.length);
  const next = requested as unknown as Record<string, unknown>;
  const previous = stored as unknown as Record<string, unknown>;
  const omittedSettings: string[] = [];
  let changed = false;
  for (const key of new Set([...Object.keys(next), ...Object.keys(previous)])) {
    if (PRESLINK_DATA_FIELDS.has(key)) continue;
    if (next[key] === undefined) {
      if (!isEmptySetting(previous[key])) omittedSettings.push(key);
      continue;
    }
    const [left, right] =
      key === "chartFormat"
        ? [
            sharedSeriesFormat(next[key], seriesCount),
            sharedSeriesFormat(previous[key], seriesCount),
          ]
        : [next[key], previous[key]];
    if (isEmptySetting(left) && isEmptySetting(right)) continue;
    if (JSON.stringify(canonicalize(left)) !== JSON.stringify(canonicalize(right))) {
      changed = true;
    }
  }
  return { changed, omittedSettings };
}

/**
 * A call on a chart this tool created that changes no chart setting is a data
 * update. A PresLink refresh makes it in place and keeps the chart's workbook
 * and formatting, which a rebuild would replace.
 */
function dataOnlyRefreshRedirect(
  input: PreslinkChartToolInput,
  parameters: FactsetChartParameters,
  slideId: string,
  targetShape: ShapeContent | undefined,
  replacedShapes: readonly ShapeContent[],
  ctx: AgentToolContext,
): PreslinkChartToolResult | undefined {
  const storedRaw = targetShape ? readFreshCreatedChartParameters(targetShape) : undefined;
  if (!storedRaw) return undefined;
  let stored: FactsetChartParameters;
  try {
    stored = normalizeActiveGraphChartParameters(storedRaw as unknown as FactsetChartParameters);
  } catch {
    return undefined;
  }
  const { changed, omittedSettings } = compareChartSettings(parameters, stored);
  if (changed) return undefined;

  // A second refresh in a turn is skipped yet reported as done, so sending the
  // model to apply_preslink then would leave the chart silently unchanged.
  const refreshAlreadyRan = ctx.preslinkProcessCompleted === true;
  const sentences = [
    "No chart setting changed, so this linked FactSet chart was not rebuilt.",
    refreshAlreadyRan
      ? "A PresLink refresh already ran in this turn and runs only once per turn, so this chart's data was not changed. Do not rebuild it; tell the user its data can be updated in a follow-up request."
      : 'Update its data in place: call apply_preslink with operation:"refresh" on these shapes, together with every other linked shape this request updates, in one call.',
  ];
  if (omittedSettings.length > 0) {
    sentences.push(
      `This call left out ${omittedSettings.join(", ")}, which counts as unchanged. To remove a setting, send it explicitly empty, such as stylesetId "", annotations [], or chartFormat {}.`,
    );
  }
  if (hasFrameFields(input.target)) {
    sentences.push(
      "Do not rebuild a linked chart to move or resize it; tell the user they can drag or resize it in PowerPoint.",
    );
  } else if (!input.target?.shapeId) {
    sentences.push(
      "This chart was the selected shape. If the user wants an additional chart rather than a change to this one, ask where to place it; a frame without shapeId adds a new chart.",
    );
  }
  return result({
    ok: false,
    code: "preslink_chart_use_refresh",
    stage: "preflight",
    message: sentences.join(" "),
    data: {
      requiredTool: "apply_preslink",
      operation: "refresh",
      shapes: replacedShapes.map((shape) => ({ slide_id: slideId, shape_id: shape.id })),
      refreshAlreadyRanThisTurn: refreshAlreadyRan,
    },
  });
}

async function readTurnPresentationContext(
  ctx: AgentToolContext,
): Promise<PreslinkCreationPresentationContext> {
  if (ctx.preslinkTurnPresentationContext) {
    return ctx.preslinkTurnPresentationContext;
  }
  const pending = getPreslinkCreationPresentationContextViaIntents(withLocalPathRedaction(ctx.log));
  ctx.preslinkTurnPresentationContext = pending;
  return pending;
}

interface PreparedWorkbook {
  workbookBase64: string;
  /** What gets inserted: create-data's images, or the renders when the workbook has linked pictures. */
  outputs: PreslinkCreatedShape[];
  addedNames: string[];
  fullPath: string;
  fileName: string;
}

/**
 * Runs create-data, patches the `_bdm` names its tags reference, and proves the
 * result renders — all before anything touches the disk. The workbook
 * create-data returns can lack the `_bdm` defined names its own tags point at.
 * `/render` then rejects the tag, but `/refresh` reports success with a stale
 * image, so `/render` returning one image per tag is the only trustworthy check.
 */
async function prepareWorkbookForPath(
  sourceWorkbookBase64: string,
  targets: PreslinkWorkbookTarget[],
  frame: PreslinkWorkbookGeometry,
  fullPath: string,
  fileName: string,
  overlayTextColor: string | undefined,
  signal: AbortSignal | undefined,
): Promise<PreparedWorkbook> {
  const requests = buildCreateDataShapes(targets, fullPath, frame);

  assertNotCancelled(signal, "create_data");
  const created = await createPreslinkData(
    {
      shapes: requests,
      mimeType: "image/png",
      monitorScale: 1,
      excelFiles: [{ file: fullPath, contents: sourceWorkbookBase64 }],
    },
    { signal },
  );

  assertNotCancelled(signal, "patch_names");
  const returnedWorkbook = created.excelFiles[0];
  if (!returnedWorkbook) {
    throw new PreslinkCreateDataError(
      "preslink_create_data_invalid_response",
      "Preslink service returned no workbook to save.",
    );
  }
  const patched = await addMissingPreslinkSheetNames(
    returnedWorkbook.contents,
    requests,
    created.shapes,
  );

  assertNotCancelled(signal, "verify_render");
  const pictures = await findLinkedPictures(patched.workbookBase64);
  let workbookBase64 = pictures.length
    ? await refreshLinkedPictureSnapshots(patched.workbookBase64, pictures, fullPath, overlayTextColor, signal)
    : patched.workbookBase64;
  // Recolored only after the snapshots: Preslink draws a range's unfilled
  // cells white, so light text would vanish from them.
  if (pictures.length && overlayTextColor) {
    workbookBase64 = await recolorLinkedPictureText(workbookBase64, pictures, overlayTextColor);
  }

  // Render the exact bytes that will be written, not the pre-patch workbook.
  const rendered = await renderPreslinkTags(
    {
      tags: created.shapes.map((shape) => shape.tags),
      mimeType: "image/png",
      excelFiles: [{ file: fullPath, contents: workbookBase64 }],
    },
    { signal },
  );
  // create-data drew linked pictures from their stale snapshots.
  const outputs = pictures.length
    ? created.shapes.map((shape, index) => ({
        ...shape,
        image: rendered.shapes[index]?.image ?? shape.image,
      }))
    : created.shapes;

  return {
    workbookBase64,
    outputs,
    addedNames: patched.addedNames,
    fullPath,
    fileName,
  };
}

/**
 * Redraws each linked picture's stored snapshot from its cell range, so the
 * slide image and the saved workbook show the requested company rather than
 * the template's sample data.
 */
async function refreshLinkedPictureSnapshots(
  workbookBase64: string,
  pictures: PreslinkLinkedPicture[],
  fullPath: string,
  darkTextColor: string | undefined,
  signal: AbortSignal | undefined,
): Promise<string> {
  const ranges = await createPreslinkData(
    {
      shapes: pictures.map((picture) => ({
        targetName: picture.rangeTarget,
        targetSheet: picture.rangeSheet,
        targetFile: fullPath,
      })),
      mimeType: "image/png",
      monitorScale: 1,
      excelFiles: [{ file: fullPath, contents: workbookBase64 }],
    },
    { signal },
  );
  const images = await Promise.all(
    ranges.shapes.map((shape) => toTransparentSnapshotPng(shape.image, darkTextColor)),
  );
  return replaceLinkedPictureSnapshots(
    workbookBase64,
    pictures.map((picture, index) => ({ ...picture, image: images[index] })),
  );
}

interface SavedWorkbook extends PreparedWorkbook {
  placements: PreslinkOutputPlacement[];
}

/**
 * Writes with `overwrite:false` and, on a typed already-exists result only,
 * rebuilds the name from the next UTC second and re-runs create-data against
 * the original in-memory workbook. Any other outcome stops: an untyped failure
 * could be a permission or locking problem, where retrying under a new name
 * would just produce a second doomed write.
 */
async function saveWorkbookWithCollisionRetry(
  sourceWorkbookBase64: string,
  targets: PreslinkWorkbookTarget[],
  frame: PreslinkWorkbookGeometry,
  location: PreslinkDeckLocation,
  identifiers: readonly string[],
  chartCode: string,
  timestamps: { next(): Date },
  overlayTextColor: string | undefined,
  signal: AbortSignal | undefined,
  log: ((message: string) => void) | undefined,
): Promise<SavedWorkbook> {
  for (let attempt = 0; attempt < MAX_SAVE_ATTEMPTS; attempt += 1) {
    assertNotCancelled(signal, "save");
    const naming = buildPreslinkWorkbookFileName({
      location,
      identifiers,
      chartCode,
      timestamp: timestamps.next(),
    });
    const prepared = await prepareWorkbookForPath(
      sourceWorkbookBase64,
      targets,
      frame,
      naming.fullPath,
      naming.fileName,
      overlayTextColor,
      signal,
    );

    assertNotCancelled(signal, "save");
    const outcome = await writeFileFromBase64WithOutcomeViaIntents({
      filePath: prepared.fullPath,
      base64File: prepared.workbookBase64,
      overwrite: false,
      log,
    });

    if (outcome.status === "written") {
      return {
        ...prepared,
        placements: layoutPreslinkOutputs(targets, frame, prepared.outputs),
      };
    }
    if (outcome.status === "already_exists") {
      log?.(
        `Preslink workbook name was taken; retrying with the next timestamp (attempt ${attempt + 1}).`,
      );
      continue;
    }
    if (outcome.status === "indeterminate") {
      const error = new PreslinkPreflightError(
        "preslink_save_indeterminate",
        "The workbook may or may not have been written, so nothing was added to the slide. Check the presentation's folder before trying again.",
      );
      throw error;
    }
    log?.(`Preslink workbook save failed: ${outcome.message}`);
    throw new PreslinkPreflightError(
      "preslink_save_failed",
      "The chart workbook could not be saved beside the presentation, so nothing was added to the slide.",
    );
  }
  throw new PreslinkPreflightError(
    "preslink_save_name_collisions",
    `The chart workbook could not be given an unused name after ${MAX_SAVE_ATTEMPTS} attempts. Nothing was added to the slide.`,
  );
}

function placementFor(saved: SavedWorkbook, outputIndex: number): PreslinkOutputPlacement {
  return (
    saved.placements.find((entry) => entry.targetIndex === outputIndex) ??
    saved.placements[outputIndex]
  );
}

function buildImageShapes(
  saved: SavedWorkbook,
  frame: PreslinkWorkbookGeometry,
  shapeIdPrefix: string,
  parameters: FactsetChartParameters,
): ShapeModification[] {
  const parametersTag = JSON.stringify(parameters);
  const frameTag = JSON.stringify(frame);
  // Created in target order so the host's creation-order z-order puts the CT21
  // overlay (Chart 2) above its base chart.
  return saved.outputs.map((output, index) => {
    const placement = placementFor(saved, index);
    return {
      id: `${shapeIdPrefix}_${index}`,
      action: "create",
      type: "image",
      imageBase64: output.image,
      imageMimeType: "image/png",
      tags: {
        ...output.tags,
        [PRESLINK_CHART_PARAMETERS_TAG]: parametersTag,
        [PRESLINK_CHART_FRAME_TAG]: frameTag,
        [PRESLINK_CHART_WORKBOOK_TAG]: saved.fileName,
      },
      left: placement.left,
      top: placement.top,
      width: placement.width,
      height: placement.height,
    } as ShapeModification;
  });
}

function geometryMatches(
  shape: ShapeContent,
  placement: PreslinkOutputPlacement,
): boolean {
  const within = (actual: number | undefined, expected: number) =>
    typeof actual === "number" && Math.abs(actual - expected) <= GEOMETRY_TOLERANCE_PT;
  return (
    within(shape.left, placement.left) &&
    within(shape.top, placement.top) &&
    within(shape.width, placement.width) &&
    within(shape.height, placement.height)
  );
}

/** PowerPoint tag names are case-insensitive; the host reports them lowercased. */
function readTag(
  tags: Record<string, string> | undefined,
  name: string,
): string | undefined {
  if (!tags) return undefined;
  const wanted = name.toLowerCase();
  const key = Object.keys(tags).find((candidate) => candidate.toLowerCase() === wanted);
  return key === undefined ? undefined : tags[key];
}

function samePath(left: string, right: string): boolean {
  // Windows and UNC paths are case-insensitive; POSIX is not, but comparing
  // case-insensitively there only risks accepting a path that differs in case,
  // which the host itself resolved, so it cannot point at a different file.
  return left.replace(/[\\/]+/g, "\\").toLowerCase() ===
    right.replace(/[\\/]+/g, "\\").toLowerCase();
}

export async function executeCreatePreslinkChartTool(
  input: PreslinkChartToolInput,
  ctx: AgentToolContext,
): Promise<PreslinkChartToolResult> {
  const log = withLocalPathRedaction(ctx.log);
  const signal = ctx.signal;

  let parameters: FactsetChartParameters;
  try {
    assertValidChartTarget(input.target, PRESLINK_TARGET_RULES);
    parameters = normalizeActiveGraphChartParameters(input.chartParameters);
    assertChartParametersValid(parameters, "create_preslink_chart", undefined, true);
  } catch (error) {
    return result({
      ok: false,
      code: error instanceof ChartCreatorValidationError ? error.code : "preslink_chart_failed",
      stage: "preflight",
      message: error instanceof Error ? error.message : String(error),
    });
  }

  // Replay comes before the indeterminate gate: an identical retry of a call
  // that already finished must get its own result back, even when that
  // result is what made the turn indeterminate.
  const ledgerKey = replayKey(parameters, input.target);
  const ledger = ctx.preslinkChartCreateResults;
  const replayed = ledger?.get(ledgerKey);
  if (replayed) {
    return replayed as PreslinkChartToolResult;
  }

  if (ctx.preslinkChartMutationIndeterminate === true) {
    return result({
      ok: false,
      code: "preslink_chart_blocked_indeterminate",
      stage: "preflight",
      message:
        "An earlier linked-chart action in this turn could not be confirmed, so no further charts will be created. Review the slide before trying again.",
    });
  }

  let stage: Stage = "preflight";
  let keptWorkbook: SavedWorkbook | undefined;
  // Flipped inside the host's prepared-batch callback, where a plain `let`
  // would stay narrowed to false at every later check.
  const insert = { dispatched: false };
  try {
    // ---- Preflight -------------------------------------------------------
    // An explicit replacement target or frame overrides the selection, so it
    // is not read: a frame without a shapeId always adds a new chart.
    const requestedFrame = hasFrameFields(input.target) ? input.target : undefined;
    const turnSlideId = findSlide(ctx.latestPresentationContent, undefined)?.id;
    const selection =
      input.target?.shapeId || requestedFrame ? [] : await readTurnSelectedShapes(ctx);
    const soleSelected = selection.length === 1 ? selection[0] : undefined;
    const targetSlideId = input.target?.slideId ?? soleSelected?.slideId ?? turnSlideId;

    // Read the target from the host, not the turn-start cache: slides and shapes
    // added earlier in this turn are missing there. The host reports a shape's
    // placeholder role only when asked, and the default frame needs it.
    const presentation = targetSlideId
      ? await extractPresentationContentViaIntents(log, {
          forceRefresh: true,
          presentationRef: ctx.presentationRef,
          filters: {
            slides: {
              ids: [targetSlideId],
              shapes: { tags: true, placeholderFormat: true, textFrame: true },
            },
          },
        })
      : undefined;
    const slide = findSlide(presentation, targetSlideId);
    if (!slide) {
      return result({
        ok: false,
        code: "preslink_chart_slide_not_found",
        stage,
        message:
          "The target slide could not be found. Observe the deck with get_slides_info and retry with a real slide id.",
      });
    }
    const explicitShape = input.target?.shapeId
      ? slide.shapes.find((shape) => shape.id === input.target?.shapeId)
      : undefined;
    if (input.target?.shapeId && !explicitShape) {
      return result({
        ok: false,
        code: "preslink_chart_target_shape_not_found",
        stage,
        message: "The shape to replace was not found on the target slide.",
      });
    }
    // Shape ids are only unique per slide, so a selection the host reported
    // without a slide id is trusted only on the slide that was current.
    const selectedOnSlide =
      soleSelected &&
      (soleSelected.slideId ? soleSelected.slideId === slide.id : slide.id === turnSlideId)
        ? slide.shapes.find((shape) => shape.id === soleSelected.id)
        : undefined;
    const selectedShape =
      selectedOnSlide && shapeFrame(selectedOnSlide) ? selectedOnSlide : undefined;
    const targetShape = explicitShape ?? selectedShape;
    const replacedShapes = targetShape ? linkedChartPictures(slide.shapes, targetShape) : [];

    // Before the Chart Creator and deck checks: a data update is redirected to
    // a refresh even when this session could not create a chart at all.
    const refreshRedirect = dataOnlyRefreshRedirect(
      input,
      parameters,
      slide.id,
      targetShape,
      replacedShapes,
      ctx,
    );
    if (refreshRedirect) return refreshRedirect;

    const mode = await resolveChartCreatorMode({ log });
    if (mode.mode !== "api") {
      return result({
        ok: false,
        code: "preslink_chart_creator_unavailable",
        stage,
        message:
          "Linked FactSet charts need the FactSet Chart Creator API, which is not available for this session.",
      });
    }

    const deckContext = await readTurnPresentationContext(ctx);
    const location = assertPreslinkDeckUsable(deckContext);
    const identifiers = readIdentifiers(parameters);
    const chartCode = readChartCode(parameters);
    const timestamps =
      ctx.preslinkChartTimestamps ??
      (ctx.preslinkChartTimestamps = createPreslinkTimestampSequence());

    // Fail before any network call when even the shortest name cannot fit.
    buildPreslinkWorkbookFileName({
      location,
      identifiers,
      chartCode,
      timestamp: new Date(0),
    });

    const placeholder =
      targetShape || requestedFrame ? undefined : emptyContentPlaceholder(slide.shapes);
    const slideSize = resolveSlideSize(presentation, ctx);
    const frame = requestedFrame
      ? explicitFrame(
          requestedFrame,
          targetShape ? { frame: replacedFrame(replacedShapes) } : undefined,
          slideSize,
        )
      : (targetShape && replacedFrame(replacedShapes)) ??
        (placeholder && shapeFrame(placeholder)) ??
        defaultFrame(slideSize);
    const placement = requestedFrame
      ? "explicit_frame"
      : explicitShape
        ? "replaced_target"
        : selectedShape
          ? "replaced_selection"
          : placeholder
            ? "content_placeholder"
            : "default";

    // ---- Styleset and download --------------------------------------------
    const source = await downloadPreslinkSourceWorkbook(parameters, {
      signal,
      onDownload: () => {
        stage = "download";
        assertNotCancelled(signal, stage);
        ctx.onProgress?.("Downloading the FactSet chart workbook");
      },
    });
    if ("failure" in source) return source.failure;

    // ---- Targets ---------------------------------------------------------
    stage = "targets";
    assertNotCancelled(signal, stage);
    const targets = await discoverPreslinkWorkbookTargets(source.workbookBase64);

    // ---- create-data, name patch, render verification, save --------------
    stage = "save";
    ctx.onProgress?.("Building and saving the linked chart workbook");
    const saved = await saveWorkbookWithCollisionRetry(
      source.workbookBase64,
      targets,
      frame,
      location,
      identifiers,
      chartCode,
      timestamps,
      source.look?.overlayTextColor,
      signal,
      log,
    );
    keptWorkbook = saved;

    // Past this point the workbook exists on disk. Every later failure keeps
    // it and names its file rather than deleting a file the user may want.
    // ---- Insert ----------------------------------------------------------
    stage = "insert";
    const confirmContext = await getPreslinkCreationPresentationContextViaIntents(log);
    if (
      confirmContext.openPresentationId !== deckContext.openPresentationId ||
      confirmContext.fullName !== deckContext.fullName
    ) {
      return finishAfterSave(ctx, result({
        ok: false,
        code: "preslink_chart_presentation_changed",
        stage,
        message: `The active presentation changed before the chart could be inserted. The workbook ${saved.fileName} was saved and kept.`,
        data: { fileName: saved.fileName },
      }));
    }

    const shapeIdPrefix = `preslink_${Date.now().toString(36)}`;
    const modifications: PresentationModification = {
      mode: "edit",
      slides: [
        {
          id: slide.id,
          action: "modify",
          shapes: buildImageShapes(saved, frame, shapeIdPrefix, parameters),
        },
      ],
    } as PresentationModification;

    assertNotCancelled(signal, stage);
    const applySummary = await applyModificationsViaIntents(modifications, log, {
      presentationRef: ctx.presentationRef,
      onPreparedBatch: (operations) => {
        insert.dispatched = operations.slides.length > 0;
      },
    });
    if (!insert.dispatched) {
      return finishAfterSave(ctx, insertFailed(saved));
    }

    // ---- Readback --------------------------------------------------------
    // Ownership is the exact link tag: it names this insert's freshly written
    // workbook, so no shape that existed before, or that the user added while
    // it was built, can carry it.
    stage = "readback";
    const after = await extractPresentationContentViaIntents(log, {
      forceRefresh: true,
      presentationRef: ctx.presentationRef,
      filters: { slides: { ids: [slide.id], shapes: { tags: true } } },
    });
    const afterShapes = findSlide(after, slide.id)?.shapes;
    if (!afterShapes) {
      return finishAfterSave(ctx, insertIndeterminate(saved, stage));
    }
    const ownedTags = new Set(saved.outputs.map((output) => readTag(output.tags, "DMSOFTTAG")));
    const owned = afterShapes.filter((shape) => {
      const linkTag = readTag(shape.tags, "DMSOFTTAG");
      return linkTag !== undefined && ownedTags.has(linkTag);
    });
    const ownedIds = owned.map((shape) => shape.id);

    if (owned.length === 0) {
      return finishAfterSave(
        ctx,
        hostRejectedEveryPicture(applySummary, saved.outputs.length)
          ? insertFailed(saved)
          : insertIndeterminate(saved, stage),
      );
    }

    const verified =
      owned.length === saved.outputs.length &&
      saved.outputs.every((output, index) => {
        const linkTag = readTag(output.tags, "DMSOFTTAG");
        const matches = owned.filter((shape) => readTag(shape.tags, "DMSOFTTAG") === linkTag);
        return matches.length === 1 && geometryMatches(matches[0], placementFor(saved, index));
      });
    if (!verified) {
      const removal = await deleteShapes(slide.id, ownedIds, ctx, log);
      if (!removal.confirmed) {
        log?.(`Could not confirm removal of the unverified linked chart: ${removal.detail}`);
      }
      const removed = removal.confirmed && insertSettled(applySummary);
      return finishAfterSave(
        ctx,
        result({
          ok: false,
          code: "preslink_chart_readback_mismatch",
          stage,
          partialMutation: true,
          message: removed
            ? `The chart could not be verified on the slide, so it was removed. The workbook ${saved.fileName} was kept.`
            : `The chart could not be verified after insertion and may still be on the slide. Review the slide before trying again. The workbook ${saved.fileName} was kept.`,
          data: { fileName: saved.fileName, applied: applySummary.applied },
        }),
        removal.confirmed ? {} : { remaining: { slideId: slide.id, shapeIds: ownedIds } },
      );
    }

    let pathConfirmed = false;
    try {
      const paths = await getPreslinkFilePathsViaIntents({
        shapeRefs: owned.map((shape) => ({
          id: shape.id,
          slideId: slide.id,
        })),
        presentationRef: ctx.presentationRef,
        log,
      });
      const reportedPaths = (paths.slides ?? [])
        .flatMap((reportedSlide) => reportedSlide.shapes ?? [])
        .flatMap((reportedShape) => reportedShape.files ?? [])
        .map((file) => file.filePath);
      pathConfirmed =
        reportedPaths.length >= owned.length &&
        reportedPaths.every((filePath) => samePath(filePath, saved.fullPath));
    } catch {
      pathConfirmed = false;
    }

    // ---- Target, selection or placeholder replacement (make before break) -
    let originalRemains = false;
    if (replacedShapes.length > 0) {
      stage = "replace_target";
      const stillReplaced = currentReplacedShapes(replacedShapes, afterShapes, ownedIds);
      const removal = await deleteShapes(slide.id, stillReplaced.ids, ctx, log);
      if (!removal.confirmed) {
        log?.(`Could not confirm removal of the replaced shape: ${removal.detail}`);
      }
      originalRemains = stillReplaced.changed || !removal.confirmed;
      if (!originalRemains && replacedShapes.some((shape) => shape.placeholderType)) {
        const known = new Set(afterShapes.map((shape) => shape.id));
        await removeEmptyPlaceholders(slide.id, (shape) => !known.has(shape.id), ctx, log);
      }
    } else if (placeholder) {
      await removeEmptyPlaceholders(slide.id, (shape) => shape.id === placeholder.id, ctx, log);
    }

    return finishAfterSave(
      ctx,
      result({
        ok: true,
        code: "preslink_chart_created",
        stage: "readback",
        applied: true,
        partialMutation: originalRemains,
        message: originalRemains
          ? `The linked chart was added, but the original shape could not be confirmed as removed and may still be on the slide. Workbook: ${saved.fileName}.`
          : `Linked FactSet chart added. Workbook: ${saved.fileName}.`,
        ...(pathConfirmed ? {} : { warning: "The host did not confirm the chart's source workbook path." }),
        data: {
          fileName: saved.fileName,
          slideId: slide.id,
          imageCount: owned.length,
          addedWorkbookNames: saved.addedNames.length,
          placement,
          frame,
          originalShapeRemains: originalRemains,
          ...(source.stylesetName ? { theme: source.stylesetName } : {}),
        },
      }),
      {
        remaining: { slideId: slide.id, shapeIds: ownedIds },
        replay: { ledger, key: ledgerKey },
      },
    );
  } catch (error) {
    if (keptWorkbook) {
      if (insert.dispatched) {
        log?.(`Linked chart insert could not be confirmed: ${error}`);
        return finishAfterSave(ctx, insertIndeterminate(keptWorkbook, stage));
      }
      if (isAbort(error)) {
        return finishAfterSave(ctx, result({
          ok: false,
          code: "preslink_chart_cancelled",
          stage,
          message: `Linked chart creation was cancelled before the chart was added to the slide. The workbook ${keptWorkbook.fileName} was saved and kept.`,
          data: { fileName: keptWorkbook.fileName },
        }));
      }
      log?.(`Linked chart insert failed before reaching the slide: ${error}`);
      return finishAfterSave(ctx, insertFailed(keptWorkbook));
    }
    if (isAbort(error)) {
      return result({
        ok: false,
        code: "preslink_chart_cancelled",
        stage,
        message: "Linked chart creation was cancelled.",
      });
    }
    if (error instanceof PreslinkPreflightError) {
      if (error.code === "preslink_save_indeterminate") {
        ctx.preslinkChartMutationIndeterminate = true;
      }
      return result({ ok: false, code: error.code, stage, message: error.message });
    }
    if (error instanceof PreslinkCreateDataError) {
      return result({ ok: false, code: error.code, stage, message: error.message });
    }
    if (error instanceof ChartCreatorValidationError) {
      return result({ ok: false, code: error.code, stage: "preflight", message: error.message });
    }
    if (isChartCreatorReauthRequired(error)) {
      return result({
        ok: false,
        code: "preslink_chart_reauth_required",
        stage,
        message:
          "FactSet still asked for a reconnect after one reconnect attempt, so no chart was created. Reconnect FactSet from the add-in, then ask again.",
      });
    }
    return result({
      ok: false,
      code: "preslink_chart_failed",
      stage,
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * An empty placeholder under the new chart shows "Click to add text" around a
 * letterboxed picture and through its transparent background. PowerPoint also
 * puts one back when a picture that filled it is deleted. Leaving it costs
 * only looks, so a failure here never fails the chart.
 */
async function removeEmptyPlaceholders(
  slideId: string,
  isCandidate: (shape: ShapeContent) => boolean,
  ctx: AgentToolContext,
  log: ((message: string) => void) | undefined,
): Promise<void> {
  try {
    const after = await extractPresentationContentViaIntents(log, {
      forceRefresh: true,
      presentationRef: ctx.presentationRef,
      filters: {
        slides: { ids: [slideId], shapes: { placeholderFormat: true, textFrame: true } },
      },
    });
    const empty = (findSlide(after, slideId)?.shapes ?? []).filter(
      (shape) =>
        isCandidate(shape) &&
        Boolean(shape.placeholderType) &&
        !PLACEHOLDER_CONTENT_TYPES.has(shape.type) &&
        !shape.text?.trim() &&
        !(shape.paragraphs ?? []).some((paragraph) => paragraph.text.trim()),
    );
    const removal = await deleteShapes(slideId, empty.map((shape) => shape.id), ctx, log);
    if (!removal.confirmed) {
      log?.(`Could not remove the empty placeholder under the chart: ${removal.detail}`);
    }
  } catch (error) {
    log?.(`Could not remove the empty placeholder under the chart: ${error}`);
  }
}

interface ShapeRemoval {
  confirmed: boolean;
  detail?: string;
}

/**
 * Confirmed only when the host reports every delete applied with nothing
 * skipped or failed. A throw, a refusal, or a row the host left without a
 * status all mean the shape may still be on the slide.
 */
async function deleteShapes(
  slideId: string,
  shapeIds: readonly string[],
  ctx: AgentToolContext,
  log: ((message: string) => void) | undefined,
): Promise<ShapeRemoval> {
  if (shapeIds.length === 0) return { confirmed: true };
  try {
    const summary = await applyModificationsViaIntents(
      {
        mode: "edit",
        slides: [
          {
            id: slideId,
            action: "modify",
            shapes: shapeIds.map((id) => ({ id, action: "delete" })),
          },
        ],
      } as PresentationModification,
      log,
      { presentationRef: ctx.presentationRef },
    );
    if (
      summary.errors.count === 0 &&
      summary.skipped.count === 0 &&
      summary.applied.byAction.delete === shapeIds.length
    ) {
      return { confirmed: true };
    }
    const issues = [...summary.errors.issues, ...summary.skipped.issues].map((issue) => issue.message);
    return {
      confirmed: false,
      detail: issues.length
        ? issues.join("; ")
        : `the host confirmed ${summary.applied.byAction.delete} of ${shapeIds.length} deletes`,
    };
  } catch (error) {
    return { confirmed: false, detail: error instanceof Error ? error.message : String(error) };
  }
}

/** The insert call returned, and every error it reported is a refused picture. */
function insertSettled(summary: ApplyResultSummary): boolean {
  return summary.errors.count === (summary.errors.byReason.SHAPE_CREATE_FAILED ?? 0);
}

/**
 * Only a host that refused every picture by name proves nothing landed. A
 * batch-level error, a timeout, or a row without a status may still have
 * inserted a picture whose link tag the readback cannot see.
 */
function hostRejectedEveryPicture(summary: ApplyResultSummary, pictureCount: number): boolean {
  return (
    insertSettled(summary) &&
    summary.applied.byAction.create === 0 &&
    (summary.errors.byReason.SHAPE_CREATE_FAILED ?? 0) >= pictureCount
  );
}

function insertFailed(saved: SavedWorkbook): PreslinkChartToolResult {
  return result({
    ok: false,
    code: "preslink_chart_insert_failed",
    stage: "insert",
    message: `The chart could not be added to the slide, so nothing was inserted. The workbook ${saved.fileName} was saved and kept.`,
    data: { fileName: saved.fileName },
  });
}

function insertIndeterminate(saved: SavedWorkbook, stage: Stage): PreslinkChartToolResult {
  return result({
    ok: false,
    code: "preslink_chart_insert_indeterminate",
    stage,
    partialMutation: true,
    message: `The chart may or may not have been added to the slide; it could not be confirmed. Review the slide before trying again. The workbook ${saved.fileName} was kept.`,
    data: { fileName: saved.fileName },
  });
}

/**
 * The shapes to replace as they stand after the insert. One the user removed
 * meanwhile is simply gone. One whose id now carries a different linked chart
 * is no longer what was asked to be replaced, so it is left alone and counted
 * as changed.
 */
function currentReplacedShapes(
  replaced: readonly ShapeContent[],
  afterShapes: readonly ShapeContent[],
  ownedIds: readonly string[],
): { ids: string[]; changed: boolean } {
  const ids: string[] = [];
  let changed = false;
  for (const original of replaced) {
    if (ownedIds.includes(original.id)) continue;
    const current = afterShapes.find((shape) => shape.id === original.id);
    if (!current) continue;
    const workbook = createdChartWorkbook(original);
    if (workbook !== undefined && createdChartWorkbook(current) !== workbook) {
      changed = true;
      continue;
    }
    ids.push(current.id);
  }
  return { ids, changed };
}

/**
 * Every outcome once the workbook is on disk goes through here: pictures that
 * may remain are kept out of native edits, a partial outcome stops further
 * linked charts this turn, and only a success is replayed.
 */
function finishAfterSave(
  ctx: AgentToolContext,
  outcome: PreslinkChartToolResult,
  options: {
    remaining?: { slideId: string; shapeIds: readonly string[] };
    replay?: { ledger: Map<string, unknown> | undefined; key: string };
  } = {},
): PreslinkChartToolResult {
  const { remaining, replay } = options;
  if (remaining && remaining.shapeIds.length > 0) {
    const bucket =
      ctx.preslinkExcludedShapeIdsBySlideId ??
      (ctx.preslinkExcludedShapeIdsBySlideId = new Map());
    const existing = bucket.get(remaining.slideId) ?? new Set<string>();
    remaining.shapeIds.forEach((id) => existing.add(id));
    bucket.set(remaining.slideId, existing);
  }
  if (outcome.partialMutation) ctx.preslinkChartMutationIndeterminate = true;
  if (outcome.ok) replay?.ledger?.set(replay.key, outcome);
  return outcome;
}
